import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Users,
  Search,
  Plus,
  Eye,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { adminService } from "@/lib/database";
import type { Admin } from "@/lib/supabase";
import { Link, useNavigate } from "react-router-dom";

export default function TeamList() {
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const itemsPerPage = 20;

  const navigate = useNavigate();

  useEffect(() => {
    loadAdmins();
  }, [currentPage]);

  const loadAdmins = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * itemsPerPage;
      const { admins: data, total } = await adminService.getAll(
        itemsPerPage,
        offset
      );

      setAdmins(data);
      setTotalAdmins(total);
      setTotalPages(Math.ceil(total / itemsPerPage));
    } catch (error) {
      console.error("Error loading admins:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAdmins = admins.filter((admin) => {
    const matchesSearch =
      admin.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      admin.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading admins...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between space-x-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            All Team Members
          </h1>
          <p className="text-gray-600">
            Manage team member accounts and information
          </p>
        </div>

        <Link to="/admin/team/new">
          <Button className="bg-[#254164] hover:bg-[#1a2d47]">
            <Plus className="h-4 w-4 mr-2" />
            New Member
          </Button>
        </Link>
      </div>

      {/* Admins List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl md:text-2xl">
                Members ({totalAdmins})
              </CardTitle>
              <CardDescription>
                All registered team members with search capabilities
              </CardDescription>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search members..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {filteredAdmins.map((admin) => (
              <div
                key={admin.id}
                className="flex flex-col md:flex-row md:items-center md:justify-between p-4 border border-gray-200 rounded-lg bg-gray-50 hover:bg-white shadow-sm transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <Users className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="font-medium text-gray-900">{admin.name}</p>
                    <p className="text-sm text-gray-600">{admin.email}</p>
                  </div>
                </div>

                <div className="flex flex-col mt-4 md:mt-0 md:flex-row md:items-center md:space-x-6">
                  <div className="flex justify-between space-x-6">
                    {admin.phone ? (
                      <div className="text-start md:text-center">
                        <p className="text-sm font-medium text-gray-900">
                          Phone
                        </p>
                        <p className="text-xs text-gray-600">{admin.phone}</p>
                      </div>
                    ) : (
                      <div className="text-start md:text-center">
                        <p className="text-sm font-medium text-gray-900">
                          Phone
                        </p>
                        <p className="text-gray-500 text-xs">
                          No phone provided
                        </p>
                      </div>
                    )}

                    <div className="text-start md:text-center">
                      <p className="text-sm font-medium text-gray-900">
                        Joined
                      </p>
                      <p className="text-xs text-gray-600">
                        {new Date(admin.created_at!).toLocaleDateString()}
                      </p>
                    </div>

                    {/* <div className="text-right md:text-center">
                        <p className="text-sm font-medium text-gray-900">Status</p>
                        <Badge className={admin.is_active ? 'bg-green-100 text-green-800 hover:bg-green-100' : 'bg-red-100 hover:bg-red-100 text-red-800'}>
                          {admin.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div> */}
                  </div>

                  <Button
                    size="sm"
                    onClick={() => {
                      navigate(`/admin/team/${admin.id}`);
                    }}
                    className="mt-4 md:mt-0"
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-6 space-x-2">
            <p className="text-sm text-gray-600">
              Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
              {Math.min(currentPage * itemsPerPage, totalAdmins)} of{" "}
              {totalAdmins} members
            </p>

            <div className="flex relative items-center justify-center pt-6 md:pt-0 space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>

              <span className="text-sm absolute md:relative top-0 text-gray-600">
                Page {currentPage} of {totalPages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                }
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
